import _ from '@/common/helpers/index';
import emptyInnerHTML from '@/common/helpers/empty-inner-html';
import ColumnTypes from './column-types';
import shortid from 'shortid';

/**
 * Cache the definition of a grid column for consumption in the rendering logic.
 */
export default class CachedColumn {
    original = null;

    header = emptyInnerHTML;

    type = ColumnTypes.text;

    field = null;

    style = null;

    value = null;

    sortable = false;

    resizeable = false;

    sticky = false;

    predicate = false;

    id = null;

    actionColumn = false;

    defaultColumn = false;

    lockedColumn = false;

    /**
     * Any properties not explicitly required are moved into
     * this property and passed down to the rendering logic of each cell
     * @type {{}}
     */
    props = {};

    /**
     * Parse the provided column definition and cache its various properties.
     * The original definition is kept as `this.original`.
     * @param {Object} columnDefinition A plain object literal defining the structure of the column.
     */
    constructor(columnDefinition) {
        this.original = {...columnDefinition};
        this.parse(columnDefinition);
    }

    /**
     * todo: create normal name
     * todo: enabled property
     * Checks if columns should be rendered.
     * You can hide action or other columns or nested columns,
     * td will be rendered, but component won't be rendered
     *
     * @param {Object} rowData
     * @returns {Boolean}
     */
    shouldRender({rowData}) {
        if (this.original.enabled) {
            return typeof this.original.enabled === 'function'
                ? this.original.enabled({
                    rowData,
                })
                : this.original.enabled;
        }
        return true;
    }

    /**
     * Parse the base column definition provided to the grid and cache its properties
     * to prevent computations on each column render.
     * @param {string} name The name is used to identify a key within the fields of a row.
     * @param {string} header Define a header string to control manually what
     * is displayed in the column header text. Set to false to use an empty string.
     * Otherwise the name is transferred.
     * @param {string} type Defines the type of the column and how it should be rendered.
     * Must be a value found within ColumnTypes.
     * @param {null|Object} style An object with custom properties to be translated
     * into additional styles.
     * @param {boolean} sortable Defines whether the column header can be clicked to
     * toggle sorting asc/desc on the column or off (3 clicks)
     * @param {boolean} resizeable Defines whether a column width can be resized. Defaults to
     * true, unless the column is locked.
     * @param {boolean} sticky Defines whether the column can be re-arranged or hidden.
     *                          Sticky columns will always precede other columns.
     * @param {string} expand Defines additional resources to fetch
     * @param {boolean} predicate Defines whether to display column (true: display | false: hide)
     * @param {null|Object} predicateOverride Defines if predicate is false, instead of hiding,
     * update cached column with new properties
     * @param {null|Object} props Any properties not explicitly required are moved into
     * this property and passed down to the rendering logic of each cell
     * @param {Function} value A function used to overwrite the value of the cell based
     * on a delegate
     * @param {boolean} actionColumn Defines whether column is for preceding actions (e.g. checkbox)
     * @param {boolean} defaultColumn Defines whether the column is included by default in the grid
     * @param {boolean} lockedColumn Defines whether the column is locked in the grid
     */
    parse({
        name = null,
        header = null,
        type = null,
        style = null,
        value = null,
        expand = null,
        sortable = false,
        resizeable = true,
        sticky = false,
        predicate = null,
        predicateOverride = null,
        actionColumn = false,
        defaultColumn = false,
        lockedColumn = false,
        ...props
    }) {
        if (type) {
            if (!ColumnTypes[type]) {
                throw new Error(`Unknown column type '${type}' was provided for ${this.header}`);
            } else {
                this.type = type;
            }
        }
        if (name) {
            // allow header to be overwritten, otherwise derive it from the column name
            this.header = header || name.split('.').map(_.unCamelCase).join(' ');
            this.field = name;
        } else {
            // if `header` is false we print an empty string, otherwise
            // use the `header` as-is, or if `null/undefined` use the default value
            this.header = header === false ? '' : (header || this.header);
        }
        this.props = props || this.props;
        this.style = style;
        this.value = value;
        this.expand = expand;
        this.sortable = sortable;
        this.resizeable = resizeable && !(actionColumn || lockedColumn);
        this.sticky = sticky;
        this.predicate = predicate;
        this.actionColumn = actionColumn;
        this.defaultColumn = defaultColumn;
        this.lockedColumn = lockedColumn;

        if (this.type === ColumnTypes.nested) {
            const columnCopy = Object.keys(this)
                .filter(key => key !== 'columns')
                .reduce((memo, key) => ({...memo, [key]: this[key]}), {});

            this.columns = this.original.columns.map(definition => new CachedColumn({
                ...columnCopy,
                ...definition,
            }));
        }
        if (this.defaultColumn) {
            this.id = this.field || `default_${this.header.replace(/\s/g, '_')}`;
        } else {
            this.id = this.field || shortid.generate();
        }
        if (this.predicate === false && predicateOverride !== null) {
            this.predicate = true;
            Object.assign(this, {...predicateOverride});
        }
    }

    getColumnClasses() {
        return {
            'fs-block': true,
            'text-blur': this.style && this.style.blur,
            'text-right': this.style && this.style.textRight,
            'text-truncate-table': this.style && this.style.truncate,
        };
    }

    getColumnProps() {
        const classes = this.getColumnClasses();
        return {class: classes};
    }
}
